import { useMutation } from '@tanstack/react-query';
import { Archive, ArchiveRestore, ChevronRight, Plus } from 'lucide-react';
import { useState } from 'react';
import { describeError } from '../../lib/api.ts';
import type { PipelinePlaceView, PipelineView } from '../../lib/client.ts';
import { usePipelines } from '../../lib/record-data.ts';
import { cn } from '../../lib/utils.ts';
import { Badge, type BadgeVariant } from '../ui/badge.tsx';
import { Button } from '../ui/button.tsx';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card.tsx';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select.tsx';
import { outcomeOf, StageStepper } from './stage-control.tsx';

const day = (iso: string) => new Date(iso).toLocaleDateString();

/** The status beside a pipeline's name: where it ended, or the stage it has been at since when. */
const STATUS: Record<ReturnType<typeof outcomeOf>, BadgeVariant> = {
  open: 'brand',
  won: 'success',
  lost: 'danger',
};

/** What a record's Pipelines card asks the API to do; each kind of record has its own routes. */
export interface PlaceActions {
  /** `PATCH {stageId}`: the stage names its pipeline, and only that place moves. */
  move: (stageId: string) => Promise<unknown>;
  setArchived: (pipelineId: string, archived: boolean) => Promise<unknown>;
  add: (body: { pipelineId: string; stageId: string }) => Promise<unknown>;
}

/**
 * A record's Pipelines card (TS-1748 for projects, then candidates in Candidate Flow): one stage
 * bar per pipeline it is in, labelled with the pipeline's name and its status, the default first.
 * A move in one bar is a move in that pipeline only (`PATCH {stageId}` — the stage names its
 * pipeline). Won bars are green and lost ones red (design-system colours, `StageStepper`); a
 * finished one (won or lost) can be archived, which folds it into "Archived" at the foot of the
 * card (restorable), still drawn as its stage bar, faded like a disabled control. "Add to pipeline" picks a pipeline the record is not in and a starting stage.
 * Taking a record out of a pipeline is deliberately not offered: archiving is how a finished
 * pipeline gets out of the way, and it keeps the place and its history.
 */
export function PipelinePlaces({
  type,
  recordName,
  places,
  canWrite,
  actions,
  onChanged,
  testId,
  placeTestId,
}: {
  type: 'project' | 'candidate';
  recordName: string;
  places: PipelinePlaceView[];
  canWrite: boolean;
  actions: PlaceActions;
  onChanged: () => Promise<void>;
  /** `data-testid` of the card and of each pipeline's section. */
  testId: string;
  placeTestId: string;
}) {
  const pipelines = usePipelines(type);
  const byId = new Map(pipelines.map((p) => [p.id, p] as const));
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const fail = (err: unknown) => setError(describeError(err));
  const ok = async () => {
    setError(null);
    await onChanged();
  };
  const move = useMutation({ mutationFn: actions.move, onSuccess: ok, onError: fail });
  const archive = useMutation({
    mutationFn: ({ pipelineId, archived }: { pipelineId: string; archived: boolean }) =>
      actions.setArchived(pipelineId, archived),
    onSuccess: ok,
    onError: fail,
  });
  const joinable = pipelines.filter((p) => !places.some((m) => m.pipelineId === p.id));
  const active = places.filter((m) => !m.archivedAt);
  const archived = places.filter((m) => m.archivedAt);
  const busy = move.isPending || archive.isPending;

  return (
    <Card data-testid={testId}>
      <CardHeader className="items-center pt-5">
        <CardTitle>Pipelines</CardTitle>
        {canWrite && joinable.length > 0 && (
          <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
            <Plus /> Add to pipeline
          </Button>
        )}
      </CardHeader>
      <CardContent className="flex flex-col pt-3 pb-4">
        {places.length === 0 && (
          <p className="text-muted-foreground py-3 text-sm">In no pipeline yet.</p>
        )}
        {active.map((m, i) => {
          const pipeline = byId.get(m.pipelineId);
          const outcome = outcomeOf(m.stage);
          return (
            <section
              key={m.pipelineId}
              className={cn('flex flex-col gap-1 py-3', i > 0 && 'border-t')}
              aria-label={m.name}
              data-testid={placeTestId}
              data-pipeline-id={m.pipelineId}
              data-outcome={outcome}
            >
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="font-medium">{m.name}</span>
                {m.isDefault && places.length > 1 && <Badge variant="secondary">Default</Badge>}
              </div>
              <div className="flex flex-wrap items-end gap-2">
                {pipeline && (
                  <div className="min-w-0 flex-1">
                    <StageStepper
                      pipeline={pipeline}
                      value={m.stageId}
                      previousStageId={m.previousStageId}
                      disabled={!canWrite || busy}
                      onMove={(stageId) => move.mutate(stageId)}
                      // Over the stage it is about, so it needs no stage name of its own.
                      marker={
                        <Badge variant={STATUS[outcome]} data-testid="stage-since">
                          {outcome === 'open'
                            ? `since ${day(m.stageEnteredAt)}`
                            : `on ${day(m.stageEnteredAt)}`}
                        </Badge>
                      }
                    />
                  </div>
                )}
                {canWrite && outcome !== 'open' && (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() => archive.mutate({ pipelineId: m.pipelineId, archived: true })}
                  >
                    <Archive /> Archive
                  </Button>
                )}
              </div>
            </section>
          );
        })}
        {archived.length > 0 && (
          <div className={cn('flex flex-col', active.length > 0 && 'border-t')}>
            <button
              type="button"
              className="text-muted-foreground hover:text-foreground flex items-center gap-1.5 py-3 text-sm"
              aria-expanded={showArchived}
              onClick={() => setShowArchived((v) => !v)}
            >
              <ChevronRight
                className={cn('size-4 transition-transform', showArchived && 'rotate-90')}
              />
              Archived ({archived.length})
            </button>
            {showArchived && (
              // The same stage bar as a live place, in its own colours but faded and not
              // movable (`archived`, drawn as a disabled control), so where it ended still reads
              // at a glance.
              <ul className="flex flex-col gap-1 pb-1" data-testid="archived-pipelines">
                {archived.map((m) => {
                  const pipeline = byId.get(m.pipelineId);
                  return (
                    <li
                      key={m.pipelineId}
                      className="bg-muted/50 flex flex-col gap-1 rounded-md px-3 py-2"
                      aria-label={m.name}
                      data-pipeline-id={m.pipelineId}
                    >
                      <div className="flex flex-wrap items-center gap-2 text-sm">
                        <span className="text-muted-foreground font-medium">{m.name}</span>
                        <span className="text-muted-foreground text-xs">
                          archived {day(m.archivedAt!)}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-end gap-2">
                        {pipeline ? (
                          <div className="min-w-0 flex-1">
                            <StageStepper
                              pipeline={pipeline}
                              value={m.stageId}
                              previousStageId={m.previousStageId}
                              archived
                              onMove={() => undefined}
                              marker={
                                <Badge variant="secondary" data-testid="stage-since">
                                  on {day(m.stageEnteredAt)}
                                </Badge>
                              }
                            />
                          </div>
                        ) : (
                          <Badge variant={STATUS[outcomeOf(m.stage)]} className="mr-auto">
                            {m.stage.name}
                          </Badge>
                        )}
                        {canWrite && (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={busy}
                            onClick={() =>
                              archive.mutate({ pipelineId: m.pipelineId, archived: false })
                            }
                          >
                            <ArchiveRestore /> Restore
                          </Button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
        {error && (
          <p className="text-danger pt-2 text-sm" role="alert">
            {error}
          </p>
        )}
      </CardContent>
      {adding && (
        <AddToPipelineDialog
          recordName={recordName}
          pipelines={joinable}
          add={actions.add}
          onClose={() => setAdding(false)}
          onAdded={async () => {
            setAdding(false);
            await ok();
          }}
        />
      )}
    </Card>
  );
}

function AddToPipelineDialog({
  recordName,
  pipelines,
  add: addPlace,
  onClose,
  onAdded,
}: {
  recordName: string;
  /** The live pipelines of its type it is not in yet. */
  pipelines: PipelineView[];
  add: PlaceActions['add'];
  onClose: () => void;
  onAdded: () => Promise<void>;
}) {
  const [pipelineId, setPipelineId] = useState(pipelines[0]!.id);
  const pipeline = pipelines.find((p) => p.id === pipelineId)!;
  const firstOpen = (p: PipelineView) => (p.stages.find((s) => !s.isTerminal) ?? p.stages[0])!.id;
  const [stageId, setStageId] = useState(firstOpen(pipeline));
  const [error, setError] = useState<string | null>(null);
  const add = useMutation({
    mutationFn: () => addPlace({ pipelineId, stageId }),
    onSuccess: onAdded,
    onError: (err) => setError(describeError(err)),
  });
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add {recordName} to a pipeline</DialogTitle>
        </DialogHeader>
        <form
          id="add-to-pipeline"
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            add.mutate();
          }}
        >
          <label className="flex flex-col gap-1 text-sm">
            Pipeline
            <Select
              value={pipelineId}
              onValueChange={(v) => {
                setPipelineId(v);
                setStageId(firstOpen(pipelines.find((p) => p.id === v)!));
              }}
            >
              <SelectTrigger aria-label="Pipeline" className="w-72 max-w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pipelines.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          <label className="flex flex-col gap-1 text-sm">
            Starting stage
            <Select value={stageId} onValueChange={setStageId}>
              <SelectTrigger aria-label="Starting stage" className="w-72 max-w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pipeline.stages.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          <p className="text-muted-foreground text-xs">
            Its stage in the pipelines it is already in does not change.
          </p>
          {error && (
            <p className="text-danger text-sm" role="alert">
              {error}
            </p>
          )}
        </form>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="add-to-pipeline" disabled={add.isPending}>
            Add to pipeline
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
